import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Icon,
    Alert,
    Button,
    CheckPicker,
    ControlLabel,
    DatePicker,
    Form,
    FormControl,
    Input,
    InputNumber,
    Schema,
    SelectPicker
} from "rsuite";
import { Collapse } from "react-collapse";
import styled, { createGlobalStyle } from "styled-components";

import { weekDaysData } from "const";
import { ButtonPrimary } from "components/base/BaseButton";
import Modal from "components/Modal/Modal";

const { NumberType, StringType } = Schema.Types;

const matchModeList = [
    { label: "Include", value: "include" },
    { label: "Exclude", value: "exclude" },
    { label: "Disabled", value: "disabled" }
];

const getMatchModeValue = (value) => {
    if (value === true) return "include";
    if (value === false) return "exclude";
    return "disabled";
};

const getMatchMode = (value) => {
    if (value === "include") return true;
    if (value === "exclude") return false;
    return null;
};

const MatchModePicker = React.forwardRef(({ value, onChange, ...props }, ref) => (
    <SelectPicker
        {...props}
        ref={ref}
        data={matchModeList}
        valueKey="value"
        labelKey="label"
        value={getMatchModeValue(value)}
        cleanable={false}
        searchable={false}
        onChange={(nextValue) => onChange(getMatchMode(nextValue))}
    />
));

MatchModePicker.displayName = "MatchModePicker";

const multilineArrayToText = (value) => {
    if (Array.isArray(value)) {
        return value.join("\n");
    }

    return typeof value === "string" ? value : "";
};

const multilineTextToArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value !== "string") {
        return [];
    }

    return value.split(/\r?\n/).filter(line => line.length > 0);
};

const INLINE_REGEXP_MODIFIER = /^\(\?([ims]+)\)/;

const getRegExpValidationParts = (pattern) => {
    const flags = new Set();
    let source = pattern;
    let modifierMatch = source.match(INLINE_REGEXP_MODIFIER);

    while (modifierMatch) {
        modifierMatch[1].split("").forEach(flag => flags.add(flag));
        source = source.slice(modifierMatch[0].length);
        modifierMatch = source.match(INLINE_REGEXP_MODIFIER);
    }

    return { source, flags: Array.from(flags).join("") };
};

const rulesAreValid = (patterns = "") => multilineTextToArray(patterns).every(pattern => {
    try {
        const { source, flags } = getRegExpValidationParts(pattern);
        new RegExp(source, flags);
        return true;
    } catch (error) {
        return false;
    }
});

const getRulePayload = (formValue) => ({
    match_phrases: formValue.match_phrases ?? null,
    phrases: multilineTextToArray(formValue.phrases),
    match_patterns: formValue.match_patterns ?? null,
    patterns: multilineTextToArray(formValue.patterns)
});

const parseTimeToDate = (timeValue) => {
    if (!timeValue || typeof timeValue !== "string") {
        return null;
    }

    const [hours = "0", minutes = "0"] = timeValue.split(":");
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);

    return date;
};

const getCheckedWeekDayIds = (weekDays = []) => {
    if (!Array.isArray(weekDays)) {
        return [];
    }

    if (weekDays.every(day => typeof day === "number")) {
        return weekDays;
    }

    return weekDays
        .filter(day => day && day.checked)
        .map(day => day.id);
};

const getEditFormValue = (rowData) => ({
    "sms.fake_dlr_id": rowData?.["sms.fake_dlr_id"],
    id: rowData?.id,
    senderid: rowData?.senderid || "",
    mcc: rowData?.mcc ?? null,
    mcc_mnc: rowData?.mcc_mnc ?? null,
    percent: rowData?.percent ?? "",
    from_time: parseTimeToDate(rowData?.from_time),
    to_time: parseTimeToDate(rowData?.to_time),
    week_days: getCheckedWeekDayIds(rowData?.week_days),
    min_calls_per_hour: rowData?.min_calls_per_hour ?? "",
    match_phrases: rowData?.match_phrases ?? null,
    phrases: multilineArrayToText(rowData?.phrases),
    match_patterns: rowData?.match_patterns ?? null,
    patterns: multilineArrayToText(rowData?.patterns)
});

const getCreateFormValue = () => {
    const fromDate = new Date();
    fromDate.setHours(0, 0, 0, 0);

    const toDate = new Date();
    toDate.setHours(23, 59, 0, 0);

    return {
        id: undefined,
        senderid: "",
        mcc: null,
        mcc_mnc: null,
        percent: "",
        from_time: fromDate,
        to_time: toDate,
        week_days: weekDaysData.map(day => day.value),
        min_calls_per_hour: "",
        match_phrases: null,
        phrases: "",
        match_patterns: null,
        patterns: ""
    };
};

const FakeDLREditModal = ({
    show,
    mode,
    rowData,
    loading,
    supplierList,
    countryList,
    operatorList,
    renderSuppliersItem,
    renderSuppliersValue,
    onSubmit,
    onTestRule,
    onClose
}) => {
    const formRef = useRef(null);
    const isCreate = mode === "create";
    const [formValue, setFormValue] = useState(() => (
        isCreate ? getCreateFormValue() : getEditFormValue(rowData)
    ));
    const [submitting, setSubmitting] = useState(false);
    const [showSmsCheck, setShowSmsCheck] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [testLoading, setTestLoading] = useState(false);
    const [testError, setTestError] = useState("");
    const formModel = useMemo(() => Schema.Model({
        id: StringType()
            .isRequired("Please select a supplier"),
        senderid: StringType()
            .maxLength(40, "The maximum is only 40 character"),
        mccMnc: StringType(),
        percent: NumberType("Wrong format")
            .min(0, "Minimum 0")
            .max(100, "Maximum 100")
            .addRule((value) => {
                if (value == null) return true;

                const rounded = Math.round(value * 10) / 10;
                return Math.abs(value - rounded) < 1e-9;
            }, "Maximum 1 decimal"),
        min_calls_per_hour: NumberType("Wrong format")
            .min(0, "Minimum 0"),
        patterns: StringType()
            .addRule(rulesAreValid, "One or more regular expressions are invalid")
    }), []);

    useEffect(() => {
        if (show) {
            setFormValue(isCreate ? getCreateFormValue() : getEditFormValue(rowData));
            setShowSmsCheck(false);
            setMessageText("");
            setTestError("");
        }
    }, [show, isCreate, rowData]);

    const handleSubmit = async () => {
        if (!formRef.current || !formRef.current.check()) {
            return;
        }

        setSubmitting(true);
        try {
            const result = await onSubmit({
                ...formValue,
                ...getRulePayload(formValue)
            }, rowData);

            if (result) {
                onClose(true);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleTestRule = async () => {
        if (!messageText.trim() || !rulesAreValid(formValue.patterns)) {
            return;
        }

        setTestLoading(true);
        setTestError("");

        try {
            const response = await onTestRule({
                ...getRulePayload(formValue),
                text: messageText
            });

            if (response?.test_result === true) {
                Alert.success("Test successful. The rules match.");
            } else {
                Alert.warning("Test unsuccessful. The rules do not match.");
            }
        } catch (error) {
            setTestError("Unable to check this SMS");
        } finally {
            setTestLoading(false);
        }
    };

    return (
        <>
            {show && <FakeDlrModalStyles />}
            <Modal
                show={show}
                title={isCreate ? "Add new Fake DLR" : "Update Fake DLR"}
                width={900}
                customMarginTop="15vh"
                extraDialogClassName="fake-dlr-edit-modal"
                footer
                successText={isCreate ? "Add new" : "Save changes"}
                // successButtonColor="blue"
                disabled={submitting || loading}
                onSuccess={handleSubmit}
                onClose={() => onClose(false)}
            >
                <StyledForm
                    ref={formRef}
                    model={formModel}
                    formValue={formValue}
                    onChange={(nextFormValue) => {
                        setFormValue(nextFormValue);
                        setTestError("");
                    }}
                    fluid
                >
                    <FullWidthField>
                        <ControlLabel>Supplier / Trunk</ControlLabel>
                        <FormControl
                            name="id"
                            accepter={SelectPicker}
                            data={supplierList}
                            valueKey="id"
                            labelKey="supplier_name"
                            renderMenuItem={renderSuppliersItem}
                            renderValue={renderSuppliersValue}
                            disabledItemValues={supplierList.filter(item => !item.trunk_id).map(item => item.id)}
                            virtualized={false}
                            cleanable={false}
                            placeholder="Supplier / Trunk..."
                        />
                    </FullWidthField>

                    <IdentityFieldsRow>
                        <Field>
                            <ControlLabel>Sender ID</ControlLabel>
                            <FormControl name="senderid" accepter={Input} placeholder="Type Sender ID..." />
                        </Field>
                        <Field>
                            <ControlLabel>Country</ControlLabel>
                            <FormControl
                                name="mcc"
                                accepter={SelectPicker}
                                data={countryList}
                                valueKey="mcc"
                                labelKey="mcc_name"
                                placeholder="All countries"
                            />
                        </Field>
                        <Field>
                            <ControlLabel>Operator</ControlLabel>
                            <FormControl
                                name="mcc_mnc"
                                accepter={SelectPicker}
                                data={operatorList}
                                valueKey="mcc_mnc"
                                labelKey="mcc_mnc_name"
                                placeholder="All operators"
                            />
                        </Field>
                    </IdentityFieldsRow>

                    <ScheduleFieldsRow>
                        <Field>
                            <ControlLabel>From time</ControlLabel>
                            <FormControl
                                name="from_time"
                                accepter={DatePicker}
                                format="HH:mm"
                                showMeridian={false}
                                ranges={[]}
                                cleanable={false}
                                placeholder="00:00"
                            />
                        </Field>
                        <Field>
                            <ControlLabel>To time</ControlLabel>
                            <FormControl
                                name="to_time"
                                accepter={DatePicker}
                                format="HH:mm"
                                showMeridian={false}
                                ranges={[]}
                                cleanable={false}
                                placeholder="23:59"
                            />
                        </Field>
                        <Field>
                            <ControlLabel>Days of week</ControlLabel>
                            <FormControl
                                name="week_days"
                                accepter={CheckPicker}
                                data={weekDaysData}
                                valueKey="value"
                                labelKey="label"
                                cleanable={false}
                                searchable={false}
                                placeholder="Days..."
                                renderValue={(value, _, selectedElement) => (
                                    value?.length === weekDaysData.length
                                        ? "All week"
                                        : selectedElement
                                )}
                            />
                        </Field>
                        <Field>
                            <ControlLabel>Fake DLR percent, %</ControlLabel>
                            <FormControl
                                name="percent"
                                accepter={InputNumber}
                                min={0}
                                max={100}
                                placeholder="0-100"
                            />
                        </Field>
                        <Field>
                            <ControlLabel>Min calls / hour</ControlLabel>
                            <FormControl
                                name="min_calls_per_hour"
                                accepter={InputNumber}
                                min={0}
                                placeholder="999"
                            />
                        </Field>
                    </ScheduleFieldsRow>

                    <RuleFieldsRow>
                        <CompactRuleField>
                            <ControlLabel>Match words/phrases</ControlLabel>
                            <FormControl
                                name="match_phrases"
                                accepter={MatchModePicker}
                                placeholder="Disabled"
                            />
                        </CompactRuleField>
                        <CompactRuleField>
                            <ControlLabel>Match patterns</ControlLabel>
                            <FormControl
                                name="match_patterns"
                                accepter={MatchModePicker}
                                placeholder="Disabled"
                            />
                        </CompactRuleField>
                    </RuleFieldsRow>

                    <RuleFieldsRow>
                        <Field>
                            <ControlLabel>Words/Phrases</ControlLabel>
                            <FormControl
                                name="phrases"
                                accepter={Input}
                                componentClass="textarea"
                                rows={4}
                                placeholder="One word or phrase per line"
                            />
                        </Field>
                        <Field>
                            <ControlLabel>Patterns</ControlLabel>
                            <FormControl
                                name="patterns"
                                accepter={Input}
                                componentClass="textarea"
                                rows={4}
                                placeholder="One regular expression per line"
                            />
                        </Field>
                    </RuleFieldsRow>

                    <CheckSmsButton
                        type="button"
                        appearance="link"
                        onClick={() => {
                            setShowSmsCheck(current => !current);
                            setTestError("");
                        }}
                    >
                        {showSmsCheck
                            ? <>
                                <Icon icon="caret-up" />
                                Hide SMS check
                            </> : <>
                                <Icon icon="caret-down" />
                                Check SMS
                            </>
                        }
                    </CheckSmsButton>

                    <Collapse isOpened={showSmsCheck}>
                        <CheckSmsPanel>
                            <FullWidthField>
                                <ControlLabel>Message text</ControlLabel>
                                <Input
                                    componentClass="textarea"
                                    rows={4}
                                    value={messageText}
                                    placeholder="Enter SMS message text"
                                    onChange={(value) => {
                                        setMessageText(value);
                                        setTestError("");
                                    }}
                                />
                            </FullWidthField>
                            <CheckSmsActions>
                                <ButtonPrimary
                                    type="button"
                                    loading={testLoading}
                                    disabled={testLoading || !messageText.trim() || !rulesAreValid(formValue.patterns)}
                                    onClick={handleTestRule}
                                >
                                    Check
                                </ButtonPrimary>
                            </CheckSmsActions>
                            {testError && <TestError>{testError}</TestError>}
                        </CheckSmsPanel>
                    </Collapse>
                </StyledForm>
            </Modal>
        </>
    );
};

export default FakeDLREditModal;

const StyledForm = styled(Form)`
    padding: 0;

    .rs-form-control-wrapper,
    .rs-picker-select,
    .rs-picker-date,
    .rs-input-number {
        width: 100%;
    }

    textarea.rs-input {
        min-height: 80px;
        height: 80px;
    }
`;

const FieldsRow = styled.div`
    display: grid;
    gap: 16px;
    margin-bottom: 14px;

    @media (max-width: 767px) {
        grid-template-columns: 1fr;
        gap: 12px;
    }
`;

const IdentityFieldsRow = styled(FieldsRow)`
    grid-template-columns: 168px repeat(2, minmax(0, 1fr));

    @media (max-width: 767px) {
        grid-template-columns: 1fr;
    }
`;

const ScheduleFieldsRow = styled(FieldsRow)`
    grid-template-columns: 112px 112px minmax(180px, 1fr) 168px 168px;

    @media (max-width: 899px) and (min-width: 768px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 767px) {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div`
    min-width: 0;

    .rs-control-label {
        display: block;
        margin-bottom: 5px;
    }
`;

const FullWidthField = styled(Field)`
    margin-bottom: 14px;
`;

const RuleFieldsRow = styled(FieldsRow)`
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (max-width: 767px) {
        grid-template-columns: 1fr;
    }
`;

const CompactRuleField = styled(Field)`
    width: 168px;
    max-width: 100%;
`;

const CheckSmsButton = styled(Button)`
    && {
        display: inline-flex;
        align-items: center;
        padding: 2px 10px;
        margin: -4px 0 10px 0;
        color: var(--color-text) !important;
        text-decoration: none !important;
        background: transparent !important;
    }

    && .rs-icon {
        margin-right: 5px;
    }
`;

const CheckSmsPanel = styled.div`
    position: relative;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;

    ${FullWidthField} {
        margin-bottom: 10px;
    }
`;

const CheckSmsActions = styled.div`
    display: flex;
    justify-content: flex-end;

    .rs-btn {
        min-width: 0;
        padding: 6px 12px;
        box-shadow: none;
        background: #2878e8;
        border-color: #2878e8;
    }
`;

const TestError = styled.div`
    margin-top: 12px;
    color: var(--color-error2);
`;

const FakeDlrModalStyles = createGlobalStyle`
    .rs-modal-dialog.fake-dlr-edit-modal {
        .rs-modal-content {
            padding: 22px 24px 16px;
            border-radius: 8px;
        }

        .rs-modal-header {
            position: relative !important;
            top: auto !important;
            width: auto !important;
            padding: 0 28px 0 0 !important;

            .rs-modal-title {
                color: var(--color-text) !important;
                font-size: 20px;
                font-weight: 600;
            }

            .rs-modal-header-close {
                top: 2px !important;
                right: 0 !important;
                color: #5f6670 !important;
            }
        }

        .rs-modal-body {
            max-height: calc(100vh - 170px);
            padding: 20px 0 12px !important;
        }
    }
`;
